import { useRef, useState } from 'react';
import { Camera, Upload, X, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { usePhotoUpload } from '@/hooks/usePhotoUpload';

interface ClientPhotoUploadProps {
  clientId?: string;
  siteId?: string | null;
  currentPhotoUrl?: string;
  onPhotoUploaded: (url: string) => void;
  clientName?: string;
  disabled?: boolean;
}

export const ClientPhotoUpload = ({
  clientId,
  siteId,
  currentPhotoUrl,
  onPhotoUploaded,
  clientName = 'Client',
  disabled = false
}: ClientPhotoUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentPhotoUrl || null);
  const { uploadPhoto, uploading } = usePhotoUpload();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    // If we have a clientId, upload to storage
    if (clientId) {
      const photoUrl = await uploadPhoto(file, clientId, siteId);
      if (photoUrl) {
        onPhotoUploaded(photoUrl);
      } else {
        // Reset preview if upload failed
        setPreviewUrl(currentPhotoUrl || null);
      }
      URL.revokeObjectURL(objectUrl);
    } else {
      // For new clients without ID, convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        onPhotoUploaded(base64String);
        URL.revokeObjectURL(objectUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPreviewUrl(null);
    onPhotoUploaded('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  const getInitials = () => {
    const parts = clientName.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return clientName.substring(0, 2).toUpperCase();
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        <Avatar className="h-32 w-32 border-4 border-border">
          <AvatarImage src={previewUrl || undefined} alt={clientName} />
          <AvatarFallback className="bg-muted">
            <User className="h-1/2 w-1/2 text-muted-foreground" />
          </AvatarFallback>
        </Avatar>
        
        {previewUrl && !disabled && (
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -top-2 -right-2 h-8 w-8 rounded-full"
            onClick={handleRemovePhoto}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex gap-2">
        {/* Camera input for mobile devices */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || uploading}
        />
        
        {/* File input for gallery/file upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || uploading}
        />
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => cameraInputRef.current?.click()}
          disabled={disabled || uploading}
        >
          {uploading ? (
            <>Uploading...</>
          ) : (
            <>
              <Camera className="h-4 w-4 mr-2" />
              Camera
            </>
          )}
        </Button>
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload
        </Button>
      </div>

      <p className="text-sm text-muted-foreground text-center">
        Take a photo or upload from gallery
      </p>
    </div>
  );
};
